import urlJoin from 'url-join';
import type { Whitelabel } from '@openticket/lib-whitelabels';
import { rudderAnalyticsTrack } from './rudderstack';

export const SINGLE_SIGN_ON_ACTION = {
    AUTO_REDIRECT: 'auto-redirect' as const,
    HIDE: 'hide' as const,
    SHOW: 'show' as const,
} as const;

export const CREDENTIALS_ACTION = {
    SHOW: 'show' as const,
    HIDE: 'hide' as const,
    HIDE_SIGNUP: 'hide-signup' as const,
};

export const REFERRER_ACTION = {
    LOGOUT: 'logout' as const,
} as const;

export type SingleSignOnAction = typeof SINGLE_SIGN_ON_ACTION[keyof typeof SINGLE_SIGN_ON_ACTION];
export type SingleSignOnActionOverride = SingleSignOnAction | `no-${SingleSignOnAction}`;

export type CredentialsAction = typeof CREDENTIALS_ACTION[keyof typeof CREDENTIALS_ACTION];
export type CredentialsActionOverride = CredentialsAction | `no-${CredentialsAction}`;

export interface SignInProvider {
    provider: string;
    action?: SingleSignOnAction;
}

export interface SingleSignOnActionOverrides {
    [provider: string]: SingleSignOnActionOverride;
}

export function resolveSignInAction(whitelabel: Whitelabel): SignInProvider | null {
    if (!whitelabel.login.sso) {
        return null;
    }

    // Multiple actions can adjust the way single-sign-on is handled.
    // When returning from a sso-provider, the auth service adds a sso_provider query parameter to recognize this.
    // When a down-stream application logs-out and redirects back to the login app, any automatic sso-redirection will result
    // in a new successful login. Effectively negating the logout action. The referrer_action can be set to 'logout'
    // to prevent this. (DD-4E8A)
    // Query parameters can be used to override the configuration values for credential and sso login. This is most useful for
    // showing the credentials input, when normally auto-redirect would immediately redirect to the sso-provider.

    const params = new URLSearchParams(window.location.search);
    const ssoProviderReturn: string | null = params.get('sso_provider');
    const referrerAction: string | null = params.get('referrer_action');
    const ssoOverrides = getSingleSignOnActionOverrides(); // query: login_sso[{provider}]={action}

    const providers = whitelabel.login.sso satisfies Record<string, { action: string }>;

    // DD-4E8B - Sign in overrides can only be applied on sso providers which are configured for a whitelabel.
    // If an override is wanted for a default action, the provider must be explicitly configured first.
    for (const [ provider, { action } ] of Object.entries(providers)) {
        const resolvedAction = combineSingleSignOnActions(action, ssoOverrides[`login_sso[${provider}]`]);

        if (resolvedAction === SINGLE_SIGN_ON_ACTION.AUTO_REDIRECT) {
            if (ssoProviderReturn === provider) {
                // DD-4E88 - The redirect has already happened, this will prevent an automatic redirect loop.
                return null;
            }

            if (referrerAction === REFERRER_ACTION.LOGOUT) {
                // DD-4E8A - While auto-redirect after an explicit user logout is theoretically problematic, the current SSO
                // provider has a session duration of 10 minutes. Until that changes or more SSO providers are added,
                // this (preventative) feature does not need to be implemented.
                //
                // The referrer action is not linked to a specific SSO provider,
                // therefore, all auto-redirects should be suppressed,
                // but an early return would make future maintenance on this code harder.
                continue;
            }

            return { provider, action: SINGLE_SIGN_ON_ACTION.AUTO_REDIRECT };
        }
    }

    return null;
}

export function redirectToSSOProvider(whitelabel: Whitelabel, { provider, action }: SignInProvider): void {
    const inParams = new URLSearchParams(window.location.search);

    const clientId: string | null = inParams.get('client_id');
    const state: string | null = inParams.get('state');
    const redirectUri: string | null = inParams.get('redirect_uri') || inParams.get('redirect_url');

    if (!clientId || !state) {
        throw new Error('Missing required parameters client_id and state in signInAction redirect URL query params.');
    }

    const outParams: { state: string; client_id: string; redirect_uri?: string } = {
        client_id: clientId,
        state,
    };

    if (redirectUri) {
        outParams.redirect_uri = redirectUri;
    }

    const url = urlJoin(
        whitelabel.login.auth_api_url,
        'sessions',
        'sso',
        provider,
        'login',
        `?${new URLSearchParams(outParams).toString()}`,
    );

    try {
        rudderAnalyticsTrack('login sso', { provider, action: action ?? 'manual' });
    } catch (e) {
        // TODO log
    }

    window.location.href = url;
}

export function isSingleSignOnAction(value: string): value is SingleSignOnAction {
    return (Object.values(SINGLE_SIGN_ON_ACTION) as string[]).includes(value);
}

export function isSingleSignOnActionOverride(value: string): value is SingleSignOnActionOverride {
    const inverse = Object.values(SINGLE_SIGN_ON_ACTION).map((action: SingleSignOnAction) => `no-${action}`);
    return isSingleSignOnAction(value) || inverse.includes(value);
}

/**
 * Retrieve the single-sign-on overrides for SSO providers from the query string.
 *
 * This can be used to override the configured and default behavior of showing the SSO login buttons.
 *
 * e.g.:
 * For each provider, {action} can be used to override the behavior regardless of the configured or default action.
 * For each provider, no-{action} can be used to revert to the default from a specific configured sign-in action.
 *
 * - login_sso[provider]=no-auto-redirect
 * - login_sso[provider]=show
 */
export function getSingleSignOnActionOverrides(): SingleSignOnActionOverrides {
    return Object.fromEntries(Array.from(new URLSearchParams(window.location.search).entries())
        .filter((pair): pair is [string, SingleSignOnActionOverride] => /^login_sso\[.*]$/.test(pair[0]) && isSingleSignOnActionOverride(pair[1])));
}

/**
 * Combine a configured single-sign-on action with an override.
 *
 * Overrides can be used to replace the configured action with a specific action
 * or to negate the configured action. In the first case, the override replaces
 * the default and configured action. In the latter case, IF the configured case is the negated override,
 * the configured action is ignored and the default action will be performed instead.
 */
export function combineSingleSignOnActions(
    action: string,
    override?: SingleSignOnActionOverride | null,
): SingleSignOnAction | null {
    if (override && isSingleSignOnAction(override)) {
        // When the override is a 'normal' action, return it.
        return override;
    }

    if (override && isSingleSignOnActionOverride(override) && action === override.slice(3)) {
        // Only when the override negates the current action (no-...), return 'null'.
        // The default action will be performed.
        // Overrides cannot negate the default action.
        return null;
    }

    return isSingleSignOnAction(action) ? action : null;
}

export function isCredentialsAction(value: string): value is CredentialsAction {
    return (Object.values(CREDENTIALS_ACTION) as string[]).includes(value);
}

export function isCredentialsActionOverride(value: string): value is CredentialsActionOverride {
    const inverse = Object.values(CREDENTIALS_ACTION).map((action: CredentialsAction) => `no-${action}`);
    return isCredentialsAction(value) || inverse.includes(value);
}

/**
 * Retrieve the sign-in credentials override from the query string.
 *
 * This can be used to override the configured and default behavior of showing the credentials input.
 *
 * e.g.:
 * {action} can be used to override the behavior regardless of the configured or default action.
 * no-{action} can be used to revert to the default from a specific configured sign-in action.
 *
 * - login_credentials=show
 * - login_credentials=no-hide
 */
export function getCredentialsActionOverride(): CredentialsActionOverride | null {
    const override = new URLSearchParams(window.location.search).get('login_credentials');
    return override && isCredentialsActionOverride(override) ? override : null;
}

/**
 * Combine a configured credentials action with an override.
 *
 * Overrides can be used to replace the configured action with a specific action
 * or to negate the configured action. In the first case, the override replaces
 * the default and configured action. In the latter case, IF the configured case is the negated override,
 * the configured action is ignored and the default action will be performed instead.
 */
export function combineCredentialsActions(
    action: string,
    ...overrides: Array<CredentialsActionOverride | null>
): CredentialsAction | null {
    for (const override of overrides.reverse()) {
        if (override && isCredentialsAction(override)) {
            // When the override is a 'normal' action, return it.
            return override;
        }

        if (override && isCredentialsActionOverride(override) && action === override.slice(3)) {
            // Only when the override negates the current action (no-...), return 'null'.
            // The default action will be performed.
            // Overrides cannot negate the default action.
            return null;
        }
    }

    return isCredentialsAction(action) ? action : null;
}

/**
 * Returns a query string containing overrides for sign-in actions.
 *
 * e.g.:
 * login_sso[provider]=no-auto-redirect
 * login_credentials=show
 *
 * The query string can be used to propagate sign-in overrides to downstream applications.
 */
export function getSignInActionOverridesQueryString(): string {
    const params = new URLSearchParams(window.location.search);

    const overrides = Array.from(params.entries())
        .filter((pair): pair is [string, SingleSignOnActionOverride] => /^login_sso\[.*]$/.test(pair[0]) && isSingleSignOnActionOverride(pair[1]))
        .map(([ key, value ]) => `${key}=${value}`);

    const credentialsQuery = params.get('login_credentials');
    if (credentialsQuery) {
        overrides.push(`login_credentials=${credentialsQuery}`);
    }

    return overrides.length ? `?${overrides.join('&')}` : '';
}
